/**
 * @module util/xhr
 *
 * HTTP utility backed by native Fetch API.
 *
 * NESTED PAYLOAD SUPPORT:
 * Nested objects are automatically detected and sent as JSON:
 *   postJson(url, {
 *     data: { user: { name: 'John', address: { city: 'Vienna' } } }
 *   }, resolve, reject);
 *   // Auto-detected as nested → Content-Type: application/json
 *
 * You can force JSON mode with contentType: 'application/json':
 *   postJson(url, {
 *     data: { jobIds: [1,2,3], deviceCategory: 'mobile' },
 *     contentType: 'application/json'
 *   }, resolve, reject);
 *
 */

import { getCsrfToken } from 'util/csrf';
import { serializeParams } from './url';

/**
 * @param {number} status
 * @param {string} [statusText]
 * @param {Array|undefined} [errors]
 * @returns {Error}
 */
function createHttpError(status, statusText = '', errors) {
    let message;

    if (status === 0) {
        message = statusText || 'Network Error';
    } else if (statusText) {
        message = `HTTP ${status} ${statusText}`;
    } else {
        message = `HTTP ${status}`;
    }

    const error = new Error(message);
    error.status = status;
    error.statusText = statusText;
    if (errors !== undefined) {
        error.errors = errors;
    }

    return error;
}

/**
 * Core HTTP request function backed by native Fetch API.
 *
 * Features:
 * - Auto-extracts `response.data` when present in JSON responses
 * - Auto-rejects with `errors` array when present
 * - Auto-injects CSRF token and X-Requested-With headers
 * - Supports FormData without serialization
 * - Provides abort() method via AbortController
 *
 * @param {string} url - The URL to request
 * @param {object} requestSettings - Configuration object
 * @param {string} [requestSettings.method='GET'] - HTTP method
 * @param {object} [requestSettings.headers] - Custom headers
 * @param {*} [requestSettings.data] - Request body (object, string, or FormData)
 * @param {string} [requestSettings.dataType] - Expected response type ('json' for auto-parsing)
 * @param {boolean} [requestSettings.crossDomain=false] - Skip X-Requested-With header
 * @param {string} [requestSettings.contentType] - Content-Type header (use 'application/json' to force JSON; nested objects auto-detected)
 * @param {function} [requestSettings.onUploadProgress] - Upload progress callback (not yet used or supported)
 * @param {function} resolve - Success callback
 * @param {function} reject - Error callback (receives Error with status, statusText, errors? properties)
 * @returns {object} Object with abort() method for cancelling the request
 */
function request(url, requestSettings, resolve, reject) {
    // Create AbortController for cancellation support
    const controller = new AbortController();

    const settings = {
        method: 'GET',
        data: null,
        dataType: null,
        crossDomain: false,
        contentType: 'application/x-www-form-urlencoded; charset=UTF-8',
        onUploadProgress: null,
        ...requestSettings,
        headers: {
            'X-CSRF-TOKEN': getCsrfToken(),
            ...requestSettings?.headers,
        },
    };

    // Add X-Requested-With header unless crossDomain or already set
    if (!settings.crossDomain && !settings.headers['X-Requested-With']) {
        settings.headers['X-Requested-With'] = 'XMLHttpRequest';
    }

    // Prepare request body
    let body = settings.data;

    // FormData must not be serialized
    if (settings.data && !(settings.data instanceof FormData)) {
        // Auto-detect nested objects and use JSON serialization
        const hasNestedObjects = typeof settings.data === 'object' && Object.values(settings.data).some(
            value => value !== null && typeof value === 'object' && !Array.isArray(value)
        );

        if (hasNestedObjects || settings.contentType === 'application/json') {
            // Use JSON for nested objects or when explicitly requested
            settings.headers['Content-Type'] = 'application/json';
            body = JSON.stringify(settings.data);
        } else {
            // Default: URL-encoded format for flat objects (backwards compatible)
            settings.headers['Content-Type'] = settings.contentType;

            if (typeof settings.data === 'object') {
                body = serializeParams(settings.data);
            }
        }
    }

    // Execute fetch request
    fetch(url, {
        method: settings.method,
        headers: settings.headers,
        body: body || undefined,
        signal: controller.signal,
    })
        .then(async (response) => {
            // Handle JSON dataType
            if (settings.dataType === 'json') {
                const text = await response.text();
                let responseJson = null;

                try {
                    responseJson = JSON.parse(text);
                } catch {
                    // Empty body or invalid JSON - fall through with raw text
                }

                if (responseJson) {
                    // Auto-extract .data property if present
                    if (responseJson.data) {
                        if (typeof resolve === 'function') {
                            resolve(responseJson.data);
                        }
                        return;
                    }

                    // Auto-reject with errors array
                    if (responseJson.errors) {
                        if (typeof reject === 'function') {
                            reject(createHttpError(response.status, response.statusText, responseJson.errors));
                        }
                        return;
                    }
                }

                if (response.ok) {
                    if (typeof resolve === 'function') {
                        resolve(text);
                    }
                } else if (typeof reject === 'function') {
                    reject(createHttpError(response.status, response.statusText));
                }
                return;
            }

            // Handle success/error status codes
            const text = await response.text();
            if (response.ok) {
                if (typeof resolve === 'function') {
                    resolve(text);
                }
            } else if (typeof reject === 'function') {
                reject(createHttpError(response.status, response.statusText));
            }
        })
        .catch((error) => {
            // Handle abort and network errors
            if (typeof reject === 'function') {
                reject(createHttpError(0, error.message || 'Network Error'));
            }
        });

    // Upload progress warning - infrastructure exists but not yet implemented with fetch (unused)
    if (typeof settings.onUploadProgress === 'function') {
        console.warn('onUploadProgress is not yet supported with fetch-based xhr implementation');
    }

    // Return object with abort() method to maintain API compatibility
    return {
        abort: () => controller.abort(),
    };
}

/**
 * GET request expecting JSON response.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [argumentSettings] - Optional settings
 * @param {function} resolve - Success callback (receives extracted data)
 * @param {function} reject - Error callback
 * @returns {object} Object with abort() method
 */
function getJson(url, argumentSettings, resolve, reject) {
    const settings = argumentSettings || {};
    settings.dataType = 'json';
    settings.method = 'GET';
    return request(url, settings, resolve, reject);
}

/**
 * POST request expecting JSON response.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [argumentSettings] - Optional settings (can include data)
 * @param {function} resolve - Success callback (receives extracted data)
 * @param {function} reject - Error callback
 * @returns {object} Object with abort() method
 */
function postJson(url, argumentSettings, resolve, reject) {
    const settings = argumentSettings || {};
    settings.dataType = 'json';
    settings.method = 'POST';
    return request(url, settings, resolve, reject);
}

/**
 * PATCH request expecting JSON response.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [argumentSettings] - Optional settings (can include data)
 * @param {function} resolve - Success callback (receives extracted data)
 * @param {function} reject - Error callback
 * @returns {object} Object with abort() method
 */
function patchJson(url, argumentSettings, resolve, reject) {
    const settings = argumentSettings || {};
    settings.dataType = 'json';
    settings.method = 'PATCH';
    return request(url, settings, resolve, reject);
}

/**
 * PUT request expecting JSON response.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [argumentSettings] - Optional settings (can include data)
 * @param {function} resolve - Success callback (receives extracted data)
 * @param {function} reject - Error callback
 * @returns {object} Object with abort() method
 */
function putJson(url, argumentSettings, resolve, reject) {
    const settings = argumentSettings || {};
    settings.dataType = 'json';
    settings.method = 'PUT';
    return request(url, settings, resolve, reject);
}

/**
 * DELETE request expecting JSON response.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [argumentSettings] - Optional settings
 * @param {function} resolve - Success callback (receives extracted data)
 * @param {function} reject - Error callback
 * @returns {object} Object with abort() method
 */
function deleteJson(url, argumentSettings, resolve, reject) {
    const settings = argumentSettings || {};
    settings.dataType = 'json';
    settings.method = 'DELETE';
    return request(url, settings, resolve, reject);
}

/**
 * Promise-based wrapper around the fetch-backed request function.
 *
 * Returns a Promise with additional properties:
 * - `.abort()` method for cancelling the request
 * - `.xhr` property containing the request object
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Request settings
 * @returns {Promise} Promise that resolves with response data
 */
function requestAsync(url, settings = {}) {
    let xhrRef = null;
    const promise = new Promise((resolve, reject) => {
        xhrRef = request(url, settings, resolve, reject);
    });

    // Add abort() method and xhr property for backwards compatibility
    promise.abort = () => xhrRef && xhrRef.abort();
    promise.xhr = xhrRef;

    return promise;
}

/**
 * Promise-based GET request expecting JSON.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Optional settings
 * @returns {Promise} Promise that resolves with extracted data
 */
function getJsonAsync(url, settings = {}) {
    return requestAsync(url, { ...settings, dataType: 'json' });
}

/**
 * Promise-based POST request expecting JSON.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Optional settings (can include data)
 * @returns {Promise} Promise that resolves with extracted data
 */
function postJsonAsync(url, settings = {}) {
    return requestAsync(url, { ...settings, method: 'POST', dataType: 'json' });
}

/**
 * Promise-based PATCH request expecting JSON.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Optional settings (can include data)
 * @returns {Promise} Promise that resolves with extracted data
 */
function patchJsonAsync(url, settings = {}) {
    return requestAsync(url, { ...settings, method: 'PATCH', dataType: 'json' });
}

/**
 * Promise-based PUT request expecting JSON.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Optional settings (can include data)
 * @returns {Promise} Promise that resolves with extracted data
 */
function putJsonAsync(url, settings = {}) {
    return requestAsync(url, { ...settings, method: 'PUT', dataType: 'json' });
}

/**
 * Promise-based DELETE request expecting JSON.
 * Auto-extracts `response.data` if present.
 *
 * @param {string} url - The URL to request
 * @param {object} [settings={}] - Optional settings
 * @returns {Promise} Promise that resolves with extracted data
 */
function deleteJsonAsync(url, settings = {}) {
    return requestAsync(url, { ...settings, method: 'DELETE', dataType: 'json' });
}

export {
    request, getJson, postJson, patchJson, putJson, deleteJson,
    requestAsync, getJsonAsync, postJsonAsync, patchJsonAsync, putJsonAsync, deleteJsonAsync,
};
